import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'p-pagination',
  templateUrl: './p-pagination.component.html',
  styleUrls: ['./p-pagination.component.scss'],
})
export class PPaginationComponent {
  /**
   * @Input currentPage - La page actuellement sélectionnée dans la pagination.
   * @type {number}
   * @default 1
   */
  @Input() currentPage: number = 1;

  /**
   * @Input totalPages - Le nombre total de pages disponibles pour la pagination.
   * @type {number}
   * @default 1
   */
  @Input() totalPages: number = 1;

  /**
   * @Input visiblePages - Le nombre de pages visibles autour de la page actuelle dans la pagination.
   * Par exemple, si `visiblePages` est défini sur 3 et que la page actuelle est 5, les pages 4, 5 et 6 seront visibles.
   * @type {number}
   * @default 3
   */
  @Input() visiblePages: number = 3;

  /**
   * @Output pageChanged - Événement émis lorsqu'il y a un changement de page.
   * L'événement renvoie le nouvel offset (basé sur la page sélectionnée) pour recharger les données.
   * @event pageChanged
   * @type {EventEmitter<number>}
   */
  @Output() pageChanged = new EventEmitter<number>();

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.pageChanged.emit(this.currentPage);
    }
  }

  getPages(): number[] {
    if (this.totalPages <= this.visiblePages) {
      return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    }

    const pages: number[] = [];
    const startPage = Math.max(
      1,
      Math.min(this.currentPage - 1, this.totalPages - 2),
    );
    const endPage = Math.min(this.totalPages, startPage + 2);

    // Ajout de la première page et de l'ellipse avant si nécessaire
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push(-1); // Ajout des ellipses
      }
    }

    // Ajout des pages intermédiaires
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Ajout de l'ellipse après si nécessaire et la dernière page
    if (endPage < this.totalPages - 1) {
      pages.push(-1); // Ajout des ellipses
    }
    if (endPage < this.totalPages) {
      pages.push(this.totalPages);
    }

    return pages;
  }
}
